/**
 * CalendarWidget - Extracted from HubDashboard.jsx
 * Shows calendar events with add/view functionality
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Plus, RefreshCw } from 'lucide-react';
import { Button } from '../../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CalendarWidget() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({ title: '', date: '', time: '', description: '' });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/social/events`, { withCredentials: true });
      setEvents(res.data || []);
    } catch (e) {
      console.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const addEvent = async () => {
    if (!newEvent.title || !newEvent.date) {
      toast.error('Please fill in title and date');
      return;
    }
    try {
      await axios.post(`${API}/social/events`, newEvent, { withCredentials: true });
      toast.success('Event added!');
      setShowAddEvent(false);
      setNewEvent({ title: '', date: '', time: '', description: '' });
      fetchEvents();
    } catch (e) {
      toast.error('Failed to add event');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Calendar</h2>
        <Button onClick={() => setShowAddEvent(!showAddEvent)} className="gap-2">
          <Plus className="w-4 h-4" /> Add Event
        </Button>
      </div>

      {/* Add Event Form */}
      {showAddEvent && (
        <div className="bg-purple-500/10 rounded-2xl p-6 border border-purple-500/30">
          <h3 className="font-bold mb-4">New Event</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-sm text-muted-foreground mb-1 block">Event Title</label>
              <input 
                value={newEvent.title}
                onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                placeholder="Event name..."
                className="w-full p-3 rounded-xl bg-secondary border border-border/30"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Date</label>
              <input 
                type="date"
                value={newEvent.date}
                onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                className="w-full p-3 rounded-xl bg-secondary border border-border/30"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Time (optional)</label>
              <input 
                type="time"
                value={newEvent.time}
                onChange={(e) => setNewEvent({...newEvent, time: e.target.value})}
                className="w-full p-3 rounded-xl bg-secondary border border-border/30"
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm text-muted-foreground mb-1 block">Description</label>
              <textarea 
                value={newEvent.description}
                onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                placeholder="Event details..."
                className="w-full p-3 rounded-xl bg-secondary border border-border/30 min-h-[80px]"
              />
            </div>
          </div>
          <Button onClick={addEvent} className="w-full mt-4">Add Event</Button>
        </div>
      )}

      {/* Events List */}
      {events.length === 0 ? (
        <div className="bg-card/60 rounded-3xl p-12 border border-border/30 text-center">
          <Calendar className="w-20 h-20 text-purple-400 mx-auto mb-6" />
          <h3 className="text-2xl font-bold mb-3">No Events</h3>
          <p className="text-muted-foreground">Add your first event to get started</p>
        </div>
      ) : (
        <div className="space-y-4">
          {events.map((event, i) => (
            <div key={i} className="bg-card/60 rounded-2xl p-5 border border-border/30">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-purple-500/20 flex flex-col items-center justify-center">
                  <span className="text-xs text-purple-400">{new Date(event.date).toLocaleDateString('en', { month: 'short' })}</span>
                  <span className="text-lg font-bold text-purple-400">{new Date(event.date).getDate()}</span>
                </div>
                <div className="flex-1">
                  <p className="font-bold text-lg">{event.title}</p>
                  {event.time && <p className="text-sm text-accent">{event.time}</p>}
                  {event.description && <p className="text-muted-foreground mt-1">{event.description}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
